/**
 * Formata um valor numérico para o padrão de moeda brasileiro (R$ 1.234,56).
 * @param {number|string} value - O valor a ser formatado.
 * @returns {string} - O valor formatado como moeda.
 */
export const formatCurrency = (value) => {
    const number = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(number)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(number);
};

/**
 * Formata um valor numérico para o padrão decimal brasileiro (1.234,56).
 * @param {number|string} value - O valor a ser formatado.
 * @param {number} decimals - Número de casas decimais (padrão 2).
 * @returns {string} - O valor formatado.
 */
export const formatNumber = (value, decimals = 2) => {
    const number = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(number)) return '0,00';
    return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(number);
};

/**
 * Converte e formata uma data (Date object, ISO string ou timestamp) no fuso horário de São Paulo (America/Sao_Paulo).
 * @param {Date|string|number} dateValue - A data a ser formatada
 * @param {string} formatPattern - 'DD/MM/AAAA' (padrão) | 'DD/MM/AA' | 'AAAA-MM-DD' | 'MM/DD/AAAA'
 * @param {string} timezone - Fuso horário (padrão 'America/Sao_Paulo')
 * @returns {string} - Data formatada
 */
export const formatDate = (dateValue, formatPattern = 'DD/MM/AAAA', timezone = 'America/Sao_Paulo') => {
    if (!dateValue) return '';

    // Se for string no formato YYYY-MM-DD puro sem horário, forçar parsing local sem timezone shift
    let d;
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        const [yyyy, mm, dd] = dateValue.split('-').map(Number);
        d = new Date(yyyy, mm - 1, dd);
    } else {
        d = new Date(dateValue);
    }

    if (isNaN(d.getTime())) return '';

    try {
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });

        const parts = formatter.formatToParts(d);
        const partMap = {};
        parts.forEach(p => { if (p.type !== 'literal') partMap[p.type] = p.value; });

        const day = partMap.day;
        const month = partMap.month;
        const yearFull = partMap.year;
        const yearShort = yearFull ? yearFull.slice(-2) : '';

        switch (formatPattern) {
            case 'DD/MM/AA':
                return `${day}/${month}/${yearShort}`;
            case 'AAAA-MM-DD':
            case 'YYYY-MM-DD':
                return `${yearFull}-${month}-${day}`;
            case 'MM/DD/AAAA':
            case 'MM/DD/YYYY':
                return `${month}/${day}/${yearFull}`;
            case 'DD/MM/AAAA':
            case 'DD/MM/YYYY':
            default:
                return `${day}/${month}/${yearFull}`;
        }
    } catch (e) {
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    }
};

/**
 * Converte e formata data e hora no fuso horário de São Paulo (America/Sao_Paulo).
 * @param {Date|string|number} dateValue - A data/hora a ser formatada
 * @param {string} dateFormat - 'DD/MM/AAAA' | 'DD/MM/AA' | 'AAAA-MM-DD' | 'MM/DD/AAAA'
 * @param {string} timeFormat - '24h' | '12h'
 * @param {boolean} showSeconds - exibir segundos (padrão true)
 * @param {string} timezone - Fuso horário (padrão 'America/Sao_Paulo')
 * @returns {string} - Data e hora formatadas
 */
export const formatDateTime = (dateValue, dateFormat = 'DD/MM/AAAA', timeFormat = '24h', showSeconds = true, timezone = 'America/Sao_Paulo') => {
    if (!dateValue) return '';
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return '';

    const dateStr = formatDate(d, dateFormat, timezone);

    try {
        const is12h = timeFormat === '12h';
        const options = {
            timeZone: timezone,
            hour: '2-digit',
            minute: '2-digit',
            hour12: is12h
        };
        if (showSeconds) options.second = '2-digit';

        const formatter = new Intl.DateTimeFormat('pt-BR', options);
        const timeStr = formatter.format(d);

        return `${dateStr} ${timeStr}`;
    } catch (e) {
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const seconds = String(d.getSeconds()).padStart(2, '0');
        return showSeconds ? `${dateStr} ${hours}:${minutes}:${seconds}` : `${dateStr} ${hours}:${minutes}`;
    }
};
